import { assign, combine, isString } from 'gleam-shared/helpers/nodash'

import safeParseJSON from 'gleam-shared/helpers/safeParse'
import toServerError from 'gleam-shared/network/toServerError'
import retrieveCSRF from 'gleam-shared/network/retrieveCSRF'
import { annotateError, isCancelled } from 'gleam-shared/network/isCancelled'

import { TEST_ENV } from 'gleam-shared/helpers/env'
import { MIN_ERROR_STATUS } from 'gleam-shared/network/statusCodes'

const XHR_HEADERS = { 'X-Requested-With': 'XMLHttpRequest' }
const JSON_HEADERS = { 'Content-Type': 'application/json' }

const cachedRequests = new Map()
const dedupedRequests = new Map()

export function clearCacheRegistry() {
  cachedRequests.clear() // for specs
}

export function clearDuplicateRegistry() {
  dedupedRequests.clear() // for specs
}

export function cancelRequest(key) {
  dedupedRequests.get(key)?.abort()
  dedupedRequests.delete(key)
}

export function dedupedRequest(url, options, key) {
  if (isString(options)) {
    key = options
    options = undefined
  } else if (!isString(key)) {
    key = url
  }

  if (!isString(url) || !isString(key)) {
    return Promise.reject(annotateError(new Error('dedupedRequest expects the URL and key to be strings!')))
  }

  cancelRequest(key)

  const controller = new AbortController()
  const signal = controller.signal
  dedupedRequests.set(key, controller)

  return request(url, { ...options, signal }).then((response) => {
    dedupedRequests.delete(key)
    return response
  }, (error) => {
    const cancelled = isCancelled(error)
    if (!cancelled) {
      dedupedRequests.delete(key)
    }

    return Promise.reject(annotateError(error))
  })
}

export function cachedRequest(url, options, key) {
  if (isString(options)) {
    key = options
    options = undefined
  } else if (!isString(key)) {
    key = url
  }

  if (!isString(url) || !isString(key)) {
    return Promise.reject(new Error('cachedRequest expects the URL and key to be strings!'))
  }

  if (cachedRequests.has(key)) {
    return cachedRequests.get(key)
  }

  const response = request(url, options).catch((err) => {
    cachedRequests.delete(key)
    return Promise.reject(err)
  })

  cachedRequests.set(key, response)
  return response
}

export function ResponseHandler(keepHttpResponse = false) {
  return function handleResponse(response) {
    if (!response?.status) {
      return Promise.reject(new Error(`Unexpected response is recieved: ${JSON.stringify(response)}`))
    }

    if (response.status >= MIN_ERROR_STATUS) {
      return receiveJSON(response).then((reason) => {
        return Promise.reject(toServerError(response, reason))
      }, () => {
        return Promise.reject(toServerError(response))
      })
    }

    return receiveJSON(response).then((data) => {
      if (keepHttpResponse) {
        return assign(response, { data })
      } else {
        return data
      }
    })
  }
}

export function request(url, { mode, signal, headers, priority, keepHttpResponse } = {}) {
  return fetch(url, {
    mode,
    signal,
    priority,
    headers: formatRequestHeaders(mode, headers)
  }).then(ResponseHandler(keepHttpResponse)).catch(tapError)
}

export function patch(url, body, options) {
  return postLike('PATCH', url, body, options)
}

export function post(url, body, options) {
  return postLike('POST', url, body, options)
}

export function put(url, body, options) {
  return postLike('PUT', url, body, options)
}

export function requestDeletion(url, options) {
  const body = new FormData()
  body.append('_method', 'delete')
  return post(url, body, options)
}

function postLike(method, url, body, { signal, headers, keepalive, keepHttpResponse } = {}) {
  return retrieveCSRF().then((csrfToken) => {
    return fetch(url, {
      method,
      signal,
      keepalive,
      body: formatPostBody(body),
      headers: formatPostHeaders(body, csrfToken, headers)
    }).then(ResponseHandler(keepHttpResponse)).catch(tapError)
  })
}

function isFormData(body) {
  return body instanceof FormData
}

function formatPostBody(body) {
  if (isFormData(body)) {
    return body
  } else {
    return JSON.stringify(body)
  }
}

function formatPostHeaders(body, csrfToken, headers) {
  const csrfHeaders = { 'X-CSRF-Token': csrfToken }

  if (isFormData(body)) {
    return combine(headers, csrfHeaders, XHR_HEADERS)
  } else {
    return combine(headers, csrfHeaders, XHR_HEADERS, JSON_HEADERS)
  }
}

function formatRequestHeaders(mode, headers) {
  if (mode !== 'cors') {
    return combine(headers, XHR_HEADERS)
  } else {
    return headers
  }
}

function receiveJSON(response) {
  return response.text().then((text) => {
    return safeParseJSON(text) ?? text
  })
}

function tapError(error) {
  if (!TEST_ENV) {
    console.warn(error)
  }

  return Promise.reject(error)
}
